import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import {
    Film, Loader2, CheckCircle2, AlertTriangle, X, ExternalLink,
} from "lucide-react";

// Poll while jobs are running so the progress bar feels live without
// flooding the API. 4s is fast enough for slide-by-slide updates without
// being wasteful.
const POLL_INTERVAL_MS = 4000;

// When all jobs have settled (done/failed) keep one slower refresh so the
// strip clears within ~30s of the last "done" job.
const IDLE_REFRESH_MS = 30000;

/**
 * Compact strip showing the user's in-flight + recently-finished video
 * render jobs. Lives at the top of the SME + Instructor dashboards so
 * users can walk away mid-render and come back to see progress without
 * having to re-open the exact lesson dialog.
 *
 * Returns `null` when there's nothing to show — the dashboard layout
 * stays clean on idle days.
 */
export default function VideoRenderJobsStrip() {
    const [jobs, setJobs] = useState([]);
    const [dismissedIds, setDismissedIds] = useState(() => new Set());

    const load = async () => {
        try {
            const { data } = await api.get("/me/video-render-jobs");
            setJobs(Array.isArray(data) ? data : []);
        } catch {
            // Endpoint can 401 briefly during sign-in; just keep the
            // existing list (or hide it) and try again next tick.
        }
    };

    useEffect(() => {
        load();
    }, []);

    useEffect(() => {
        const anyActive = jobs.some((j) => ["queued", "running"].includes(j.status));
        const interval = anyActive ? POLL_INTERVAL_MS : IDLE_REFRESH_MS;
        const id = setInterval(load, interval);
        return () => clearInterval(id);
    }, [jobs]);

    const visible = jobs.filter((j) => !dismissedIds.has(j.id));
    if (visible.length === 0) return null;

    return (
        <section
            className="rounded-lg border border-border bg-card/80 backdrop-blur-sm shadow-sm overflow-hidden"
            data-testid="video-render-jobs-strip"
        >
            <header className="flex items-center gap-2 px-4 py-2 border-b border-border bg-secondary/30 text-xs uppercase tracking-wider text-muted-foreground font-medium">
                <Film className="h-3.5 w-3.5" />
                Video renders
                <Badge variant="outline" className="ml-1">
                    {visible.length}
                </Badge>
            </header>
            <ul className="divide-y divide-border">
                {visible.map((j) => (
                    <JobRow
                        key={j.id}
                        job={j}
                        onDismiss={() =>
                            setDismissedIds((prev) => {
                                const next = new Set(prev);
                                next.add(j.id);
                                return next;
                            })
                        }
                    />
                ))}
            </ul>
        </section>
    );
}

function JobRow({ job, onDismiss }) {
    const { status, current, total, lesson_title, video_url, error } = job;
    const pct = total > 0 ? Math.min(100, Math.round((current / total) * 100)) : 0;
    const isActive = status === "queued" || status === "running";
    const isDone = status === "done";
    const isFailed = status === "failed";

    return (
        <li
            className="px-4 py-3 flex items-center gap-3"
            data-testid={`video-job-${job.id}`}
        >
            <div className="flex-shrink-0">
                {isActive && <Loader2 className="h-4 w-4 text-primary animate-spin" />}
                {isDone && <CheckCircle2 className="h-4 w-4 text-emerald-600" />}
                {isFailed && <AlertTriangle className="h-4 w-4 text-destructive" />}
            </div>

            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 text-sm">
                    <span className="truncate">
                        Lesson <strong className="font-medium">“{lesson_title}”</strong>
                    </span>
                    <span className="text-xs text-muted-foreground flex-shrink-0">
                        {isActive && total > 0 && `${current}/${total} slides`}
                        {isDone && "render complete"}
                        {isFailed && "render failed"}
                    </span>
                </div>
                {isActive && (
                    <Progress
                        value={pct}
                        className="h-1 mt-1.5"
                        data-testid={`video-job-progress-${job.id}`}
                    />
                )}
                {isFailed && error && (
                    <div className="text-xs text-destructive mt-1 truncate" title={error}>
                        {error}
                    </div>
                )}
            </div>

            <div className="flex items-center gap-1 flex-shrink-0">
                {isDone && video_url && (
                    <Button
                        asChild
                        size="sm"
                        variant="outline"
                        data-testid={`video-job-view-${job.id}`}
                    >
                        <a href={video_url} target="_blank" rel="noreferrer">
                            <ExternalLink className="h-3 w-3 mr-1" />
                            View
                        </a>
                    </Button>
                )}
                {!isActive && (
                    <Button
                        size="sm"
                        variant="ghost"
                        onClick={onDismiss}
                        aria-label="Dismiss"
                        data-testid={`video-job-dismiss-${job.id}`}
                    >
                        <X className="h-3.5 w-3.5" />
                    </Button>
                )}
            </div>
        </li>
    );
}
